/**
 * IssueCard - Central component displaying the focal issue in the canvas
 * Shows issue details including severity, root causes, symptoms, and business impact
 */

import React, { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, Target, Activity, DollarSign } from 'lucide-react';
import type { Issue, IssueSeverity } from '../../types/domainPack';

interface IssueCardProps {
  issue: Issue;
  onClose?: () => void;
}

// Severity colors
const SEVERITY_STYLES: Record<IssueSeverity, { border: string; badge: string; icon: string }> = {
  critical: {
    border: 'border-red-400',
    badge: 'bg-red-100 text-red-700 border-red-200',
    icon: 'text-red-500',
  },
  high: {
    border: 'border-orange-400',
    badge: 'bg-orange-100 text-orange-700 border-orange-200',
    icon: 'text-orange-500',
  },
  medium: {
    border: 'border-yellow-400',
    badge: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    icon: 'text-yellow-500',
  },
  low: {
    border: 'border-blue-400',
    badge: 'bg-blue-100 text-blue-700 border-blue-200',
    icon: 'text-blue-500',
  },
};

export const IssueCard: React.FC<IssueCardProps> = ({ issue, onClose }) => {
  const [showRootCauses, setShowRootCauses] = useState(false);
  const [showSymptoms, setShowSymptoms] = useState(false);

  const severityStyle = SEVERITY_STYLES[issue.severity] || SEVERITY_STYLES.medium;

  return (
    <div
      className={`bg-red-50 rounded-xl border-2 ${severityStyle.border} shadow-lg overflow-hidden`}
    >
      {/* Header */}
      <div className="bg-white/70 px-4 py-3 border-b border-red-100">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className={`w-5 h-5 ${severityStyle.icon}`} />
            <span className="text-xs font-semibold text-red-600 uppercase tracking-wide">
              The Issue
            </span>
          </div>
          <span
            className={`text-xs px-2 py-1 rounded-full border ${severityStyle.badge} font-medium`}
          >
            {issue.severity.charAt(0).toUpperCase() + issue.severity.slice(1)} Severity
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="text-lg font-bold text-neutral-900 mb-2">{issue.title}</h3>
        <p className="text-sm text-neutral-700 leading-relaxed">{issue.description}</p>

        {/* Root Causes - Collapsible */}
        {issue.root_causes && issue.root_causes.length > 0 && (
          <div className="mt-4">
            <button
              onClick={() => setShowRootCauses(!showRootCauses)}
              className="flex items-center gap-2 text-sm font-medium text-neutral-700 hover:text-neutral-900 transition-colors"
            >
              <Target className="w-4 h-4 text-red-500" />
              Root Causes ({issue.root_causes.length})
              {showRootCauses ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </button>
            {showRootCauses && (
              <ul className="mt-2 space-y-1 pl-6">
                {issue.root_causes.map((cause, index) => (
                  <li
                    key={index}
                    className="text-sm text-neutral-600 list-disc marker:text-red-400"
                  >
                    {cause}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Symptoms - Collapsible */}
        {issue.symptoms && issue.symptoms.length > 0 && (
          <div className="mt-3">
            <button
              onClick={() => setShowSymptoms(!showSymptoms)}
              className="flex items-center gap-2 text-sm font-medium text-neutral-700 hover:text-neutral-900 transition-colors"
            >
              <Activity className="w-4 h-4 text-orange-500" />
              Symptoms ({issue.symptoms.length})
              {showSymptoms ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </button>
            {showSymptoms && (
              <ul className="mt-2 space-y-1 pl-6">
                {issue.symptoms.map((symptom, index) => (
                  <li
                    key={index}
                    className="text-sm text-neutral-600 list-disc marker:text-orange-400"
                  >
                    {symptom}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Business Impact */}
        {issue.business_impact && (
          <div className="mt-4 p-3 bg-white/60 rounded-lg border border-red-100">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="w-4 h-4 text-red-500" />
              <span className="text-xs font-semibold text-red-600 uppercase tracking-wide">
                Business Impact
              </span>
            </div>
            <p className="text-sm text-neutral-700">{issue.business_impact}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default IssueCard;
